import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { ApiError, getOrder } from '../lib/api-client';
import { formatDateTime } from '../lib/format';
import { useI18n } from '../lib/i18n-context';

/**
 * 訂單詳情頁 — `/orders/:id`（D）。
 *
 * 由 MyOrders 之 localStorage id 進入；`GET /orders/:id` 取**即時** state
 * （localStorage 只存創單當下快照，過期 / 取消後不會更新）。
 *
 * 金額顯示為原始 numeric string（OrderResponseDto 不帶 currency；與
 * MyOrders / OrderConfirm 之顯示方式一致）。
 */
export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();

  const query = useQuery({
    queryKey: ['order', id],
    queryFn: () => {
      if (id === undefined) throw new Error('missing order id');
      return getOrder(id);
    },
    enabled: id !== undefined,
  });

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('orderDetail.title')}</h1>
        <Link to="/me/orders" className="topbar-back">
          {t('myOrders.title')}
        </Link>
      </header>

      {query.isPending && <p>{t('common.loading')}</p>}
      {query.isError && (
        <p className="error-text">
          {t('orderDetail.loadFailed')}
          {query.error instanceof ApiError
            ? (query.error.problem?.detail ?? query.error.message)
            : t('common.unknownError')}
        </p>
      )}
      {query.isSuccess && (
        <article className="card">
          <div className="kv">
            <span className="kv-label">{t('orderDetail.orderNumber')}</span>
            <span className="kv-value" style={{ fontFamily: 'monospace' }}>
              {query.data.order_number}
            </span>
          </div>
          <div className="kv">
            <span className="kv-label">{t('orderDetail.statusLabel')}</span>
            <span className="kv-value" style={{ color: 'var(--boat-primary)' }}>
              {query.data.state}
            </span>
          </div>
          <div className="kv">
            <span className="kv-label">{t('orderDetail.channelLabel')}</span>
            <span className="kv-value">{query.data.channel}</span>
          </div>
          <div className="kv">
            <span className="kv-label">{t('orderDetail.subtotal')}</span>
            <span className="kv-value">{query.data.subtotal}</span>
          </div>
          <div className="kv">
            <span className="kv-label">{t('orderDetail.total')}</span>
            <span className="kv-value" style={{ fontSize: 17 }}>
              {query.data.total}
            </span>
          </div>
          <hr className="divider" />
          <div className="kv">
            <span className="kv-label">{t('orderDetail.createdAt')}</span>
            <span className="kv-value" style={{ fontWeight: 400 }}>
              {formatDateTime(query.data.created_at, locale)}
            </span>
          </div>
          {query.data.hold_expires_at !== null && (
            <div className="kv" style={{ marginBottom: 0 }}>
              <span className="kv-label">{t('orderDetail.holdExpiresAt')}</span>
              <span className="kv-value" style={{ fontWeight: 400 }}>
                {formatDateTime(query.data.hold_expires_at, locale)}
              </span>
            </div>
          )}
        </article>
      )}
    </div>
  );
}
